import React from 'react';
import PropTypes from 'prop-types';
import { Card, CardHeader, CardContent, Alert, Stack, Typography, Chip } from '@mui/material';
import { Warning, TrendingDown, Inventory, People } from '@mui/icons-material';

/**
 * Componente de alertas básicas para el dashboard
 * Muestra alertas inteligentes basadas en los datos reales
 */
export default function AlertsCard({ statsData, productsData }) {
  const alerts = [];

  // Alerta: Ventas bajas
  if (statsData?.totalSales < 1000000) { // Menos de $1M
    alerts.push({
      type: 'warning',
      icon: <TrendingDown />,
      title: 'Ventas por debajo del promedio',
      message: 'Las ventas están por debajo del objetivo mensual',
      priority: 'media'
    });
  }

  // Alerta: Pocos productos vendidos
  if (productsData?.length < 5) {
    alerts.push({
      type: 'info',
      icon: <Inventory />,
      title: 'Diversificar productos',
      message: 'Considera agregar más productos a tu catálogo',
      priority: 'baja'
    });
  }

  // Alerta: Nuevos clientes
  if (statsData?.newCustomers > 5) {
    alerts.push({
      type: 'success',
      icon: <People />,
      title: 'Excelente captación de clientes',
      message: `${statsData.newCustomers} nuevos clientes este período`,
      priority: 'alta'
    });
  }

  // Alerta: Ticket promedio alto
  if (statsData?.avgTicket > 500000) { // Más de $500k
    alerts.push({
      type: 'success',
      icon: <TrendingDown style={{ transform: 'rotate(180deg)' }} />,
      title: 'Ticket promedio excelente',
      message: 'Tus clientes están comprando productos de alto valor',
      priority: 'alta'
    });
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'alta': return 'error';
      case 'media': return 'warning';
      case 'baja': return 'info';
      default: return 'default';
    }
  };

  if (alerts.length === 0) {
    return (
      <Card>
        <CardHeader title="Alertas Inteligentes" />
        <CardContent>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Todo está funcionando correctamente. No hay alertas en este momento.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title="Alertas Inteligentes" />
      <CardContent>
        <Stack spacing={2}>
          {alerts.map((alert, index) => (
            <Alert
              key={index}
              severity={alert.type}
              icon={alert.icon}
              action={
                <Chip
                  label={alert.priority.toUpperCase()}
                  size="small"
                  color={getPriorityColor(alert.priority)}
                  variant="outlined"
                />
              }
            >
              <Typography variant="subtitle2" fontWeight={600}>
                {alert.title}
              </Typography>
              <Typography variant="body2">
                {alert.message}
              </Typography>
            </Alert>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

AlertsCard.propTypes = {
  statsData: PropTypes.object,
  productsData: PropTypes.array,
};

AlertsCard.defaultProps = {
  statsData: {},
  productsData: [],
};
